import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle, Button, Badge } from '@alertwhale/ui'
import { useLiveSessions, useEndLiveSession, useUpdateLiveSession } from '@/hooks/useLiveSessions'
import { useApplications } from '@/hooks/useApplications'
import { Activity, Plus, Loader2, RefreshCw, Play, Square, Clock, Zap, Edit2 } from 'lucide-react'
import { formatDate, cn } from '@/lib/utils'
import { toast } from 'sonner'
import { CreateLiveSessionDialog } from '@/components/live/CreateLiveSessionDialog'
import { UpdateLiveSessionDialog } from '@/components/live/UpdateLiveSessionDialog'

export function LiveSessionsPage() {
  const [isRefreshing, setIsRefreshing] = useState(false)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [updateDialogOpen, setUpdateDialogOpen] = useState(false)
  const [selectedSession, setSelectedSession] = useState<string | null>(null)

  const { data, isLoading, refetch } = useLiveSessions({ status: 'active' })
  const { data: applications } = useApplications()
  const endSession = useEndLiveSession()

  const sessions = data || []
  const hasApps = applications && applications.length > 0

  const handleRefresh = async () => {
    setIsRefreshing(true)
    await refetch()
    setIsRefreshing(false)
  }

  const handleEndSession = async (sessionId: string) => {
    try {
      await endSession.mutateAsync({ id: sessionId, status: 'completed' })
      toast.success('Session ended successfully')
    } catch {
      toast.error('Failed to end session')
    }
  }

  const handleOpenUpdate = (sessionId: string) => {
    setSelectedSession(sessionId)
    setUpdateDialogOpen(true)
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-success/10 text-success border-success/20'
      case 'paused':
        return 'bg-warning/10 text-warning border-warning/20'
      default:
        return 'bg-muted text-muted-foreground border-muted'
    }
  }

  return (
    <>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              <span className="text-gradient">Live Sessions</span>
            </h1>
            <p className="text-muted-foreground mt-1">Manage active live activities and updates</p>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              size="sm"
              onClick={handleRefresh}
              disabled={isRefreshing}
            >
              <RefreshCw className={cn('h-4 w-4', isRefreshing && 'animate-spin')} />
              Refresh
            </Button>
            <Button
              variant="glow"
              onClick={() => setCreateDialogOpen(true)}
              disabled={!hasApps}
            >
              <Plus className="h-4 w-4" />
              Create Session
            </Button>
          </div>
        </div>

        {/* No Apps Warning */}
        {!hasApps && (
          <div className="glass-card rounded-xl p-5 border-warning/30 bg-warning/5">
            <div className="flex items-center gap-4">
              <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-warning/20 text-warning">
                <Activity className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold text-warning">Create an Application First</h3>
                <p className="text-sm text-muted-foreground">
                  You need to create an application before you can create live sessions.
                </p>
              </div>
            </div>
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">Loading live sessions...</span>
            </div>
          </div>
        ) : sessions.length === 0 ? (
          <Card className="glass-card border-0">
            <CardContent className="flex flex-col items-center justify-center py-16">
              <div className="w-16 h-16 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                <Activity className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-lg font-semibold mb-2">No active sessions</h3>
              <p className="text-muted-foreground text-center mb-6 max-w-md">
                Create a live session to display real-time updates on user devices via Live Activities (iOS) or ongoing notifications (Android).
              </p>
              <Button
                variant="glow"
                onClick={() => setCreateDialogOpen(true)}
                disabled={!hasApps}
              >
                <Plus className="h-4 w-4" />
                Create Your First Session
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {sessions.map((session) => (
              <Card key={session.id} className="glass-card border-0 group">
                <CardHeader className="pb-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-colors">
                        <Zap className="w-5 h-5 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-base group-hover:text-primary transition-colors">
                          {session.title}
                        </CardTitle>
                        {session.subtitle && (
                          <p className="text-sm text-muted-foreground">{session.subtitle}</p>
                        )}
                      </div>
                    </div>
                    <Badge variant="outline" className={cn('text-xs capitalize', getStatusColor(session.status))}>
                      {session.status === 'active' && <Play className="h-3 w-3 mr-1" />}
                      {session.status}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {/* Progress bar with glow */}
                    <div>
                      <div className="flex justify-between text-sm mb-2">
                        <span className="text-muted-foreground">Progress</span>
                        <span className="font-medium">{Math.round(session.progress * 100)}%</span>
                      </div>
                      <div className="progress-glow">
                        <div className={`w-[${Math.round(session.progress * 100)}%]`} style={{ width: `${session.progress * 100}%` }} />
                      </div>
                    </div>

                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Type</span>
                        <Badge variant="outline" className="text-xs bg-white/[0.03]">{session.type}</Badge>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Started</span>
                        <span>{formatDate(session.started_at)}</span>
                      </div>
                      {session.expires_at && (
                        <div className="flex justify-between">
                          <span className="text-muted-foreground flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            Expires
                          </span>
                          <span>{formatDate(session.expires_at)}</span>
                        </div>
                      )}
                    </div>

                    <div className="flex gap-2 pt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1"
                        onClick={() => handleOpenUpdate(session.id)}
                      >
                        <Edit2 className="h-3 w-3" />
                        Update
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        className="flex-1"
                        onClick={() => handleEndSession(session.id)}
                        disabled={endSession.isPending}
                      >
                        <Square className="h-3 w-3" />
                        End
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Create Live Session Dialog */}
      <CreateLiveSessionDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
      />

      {/* Update Live Session Dialog */}
      {selectedSession && (
        <UpdateLiveSessionDialog
          open={updateDialogOpen}
          onOpenChange={(open) => {
            setUpdateDialogOpen(open)
            if (!open) setSelectedSession(null)
          }}
          sessionId={selectedSession}
        />
      )}
    </>
  )
}
